/**
 * The light that shades for a declared directional light in Godot's shadow list. three draws a
 * shadow with several splits only on its sun path (`isSunLight`), which reads the direction from
 * the world position (`WebGLLights.js:566-571`), not from a target. So the fitter hangs this light
 * under the declared one, points it by its world matrix, and hides the declared light.
 */
import * as THREE from 'three';
import { hideLight, ownLayerMask, showLight } from './hiddenLight.js';
import { DirectionalSplitShadow } from './splitShadow.js';
export class SplitSunLight extends THREE.Light {
    /** The flag `WebGLLights` reads to put a light on the sun path (`WebGLLights.js:289`, `:566`). */
    isSunLight = true;
    /** `WebGLLights` caches by `id`, and shapes a new entry by this string (`WebGLLights.js:24`, `:100`). */
    type = 'SunLight';
    shadow = new DirectionalSplitShadow();
    constructor() {
        super();
        this.name = 'DirectionalShadowSplits';
        this.castShadow = true;
        this.matrixAutoUpdate = false;
    }
    /** The fitter writes the world matrix before each render, so the scene update leaves it. */
    updateMatrixWorld() { }
    updateWorldMatrix() { }
    dispose() {
        super.dispose();
        this.shadow.dispose();
    }
}
/**
 * Whether three gives `light` a sun shadow with this repository's slots. Every sun shadow array a
 * lookup reads is indexed in three's order of these lights.
 */
export function isSplitSun(light) {
    return light instanceof SplitSunLight;
}
/**
 * Each light's split sun. Written only by `attachSplitSun` and `releaseSplitSun`, so the fitter
 * finds a sun without a search of the light's children on every render.
 */
// eslint-disable-next-line no-restricted-syntax -- attachSplitSun writes an entry and releaseSplitSun deletes it
const splitSuns = new WeakMap();
/** Nothing for a light that has no split sun, or whose sun something else took off it. */
export function splitSunOf(light) {
    const sun = splitSuns.get(light);
    return sun?.parent === light ? sun : null;
}
/**
 * The light whose declaration `light` reads: a split sun reads the light it shades for. Nothing for
 * a split sun off its light.
 */
export function declaredLightOf(light) {
    return isSplitSun(light) ? light.parent : light;
}
/** The light's split sun, attached on the first call. The declared light stops shading then. */
export function attachSplitSun(light) {
    const existing = splitSunOf(light);
    if (existing)
        return existing;
    const sun = new SplitSunLight();
    // The sun renders on the light's own layers while the light is hidden.
    sun.layers.mask = ownLayerMask(light);
    hideLight(light);
    light.add(sun);
    splitSuns.set(light, sun);
    // A light leaves the scene before the fitter can see it go, so it lets go of its own sun's share.
    light.addEventListener('removed', releaseRemovedLight);
    return sun;
}
/** Removes the light's split sun, if any, and lets the declared light shade again. */
export function releaseSplitSun(light) {
    const sun = splitSunOf(light);
    if (!sun)
        return;
    light.removeEventListener('removed', releaseRemovedLight);
    splitSuns.delete(light);
    showLight(light);
    light.remove(sun);
    sun.dispose();
}
function releaseRemovedLight(event) {
    releaseSplitSun(event.target);
}
/**
 * Copies what the declared light shades with, and points the sun as three points the declared
 * light: from its target towards its position (`WebGLLights.js:575-582`).
 */
export function followDeclaredLight(sun, light, lightPosition, targetPosition) {
    sun.color.copy(light.color);
    sun.intensity = light.intensity;
    sun.shadow.intensity = light.shadow.intensity;
    sun.shadow.radius = light.shadow.radius;
    sun.matrixWorld.makeTranslation(lightPosition.x - targetPosition.x, lightPosition.y - targetPosition.y, lightPosition.z - targetPosition.z);
}
//# sourceMappingURL=splitSun.js.map